import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import { AnimatePresence, motion } from 'motion/react'
import * as React from 'react'
import { Button } from '../button'

export type AlertRenderer = (args: { close: () => void }) => React.ReactNode

interface AlertContextValue {
	open: (title: string, description: string, dismissButtonLabel: string, onDismiss: () => void) => void
}

const AlertContext = React.createContext<AlertContextValue | null>(null)

export function useAlert(): AlertContextValue {
	const ctx = React.useContext(AlertContext)
	if (!ctx) { throw new Error('useAlert must be used within AlertProvider') }
	return ctx
}

export function AlertProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
	const [title, setTitle] = React.useState<string | null>(null)
	const [description, setDescription] = React.useState<string | null>(null)
	const [dismissButtonLabel, setDismissButtonLabel] = React.useState<string | null>(null)
	const [onDismiss, setOnDismiss] = React.useState<() => void>(() => () => {})
	const [isOpen, setIsOpen] = React.useState<boolean>(false)

	const close = React.useCallback(() => {
		setIsOpen(false)
	}, [])

	const open = React.useCallback((t: string, d: string, dismissButtonLabel: string, onDismiss: () => void) => {
		setTitle(t)
		setDescription(d)
		setDismissButtonLabel(dismissButtonLabel)
		setIsOpen(true)
		setOnDismiss(() => onDismiss)
	}, [])

	const value = React.useMemo<AlertContextValue>(() => ({ open }), [open])

	return (
		<AlertContext.Provider value={value}>
			{children}
			<AnimatePresence mode="popLayout">
				{isOpen && (
					<Dialog static={true} open={isOpen} onClose={close} className="relative z-50">
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							className="fixed inset-0 bg-black/20 backdrop-blur-sm"
						/>
						<div className="fixed inset-0 w-screen overflow-y-auto">
							<div className="flex min-h-full items-center justify-center p-4">
								<DialogPanel
									as={motion.div}
									initial={{
										opacity: 0,
										scale: 0.95,
										transition: { duration: 0.2 },
									}}
									animate={{
										opacity: 1,
										scale: 1,
										transition: { duration: 0.2 },
									}}
									exit={{
										opacity: 0,
										scale: 0.95,
										transition: { duration: 0.2 },
									}}
									className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg ring-1 ring-grey-200/50"
								>
									<DialogTitle className="text-lg font-medium text-grey-900 mb-2">
										{title}
									</DialogTitle>
									<p className="text-sm text-grey-700 mb-6">{description}</p>
									<div className="flex gap-3 justify-end">
										<Button
											type="committal"
											size="normal"
											onClick={() => {
												onDismiss()
												close()
											}}
										>
											{dismissButtonLabel}
										</Button>
									</div>
								</DialogPanel>
							</div>
						</div>
					</Dialog>
				)}
			</AnimatePresence>
		</AlertContext.Provider>
	)
}

// Convenience content wrapper for common alert styling
export function AlertContent({
	title,
	description,
	actions,
}: {
	title?: React.ReactNode
	description?: React.ReactNode
	actions?: React.ReactNode
}): React.JSX.Element {
	return (
		<div className="flex flex-col gap-3">
			{title ? <div className="text-lg font-medium text-grey-900">{title}</div> : null}
			{description ? <div className="text-sm text-grey-700">{description}</div> : null}
			{actions ? <div className="mt-1 flex gap-3 justify-end">{actions}</div> : null}
		</div>
	)
}
